import React, { FC, PropsWithChildren, useMemo, useState } from 'react'
import styles from '../Filters.module.scss'
import Checkbox from 'components/SA/FormElements/Checkbox'
import classNames from 'classnames'
import SALink from 'components/SALink'

export type CheckBoxParentProps = {
  href?: string
  isHidden?: boolean
  isShowMoreOpen?: boolean
  dataGtmEvent?: string
  dataGtmInfo?: string
}

type CheckboxInput = {
  text: string
  checked: boolean
  onClick?: () => void
  isHidden?: boolean
  href?: string
}

export type CheckboxListFilterProps = {
  filterOptions: ReadonlyArray<CheckboxInput>
  disableShowMore?: boolean
  facetName?: string
}

// We use SALink only for SEO
const CheckBoxParent: FC<PropsWithChildren<CheckBoxParentProps>> = ({
  href,
  isHidden,
  isShowMoreOpen,
  dataGtmEvent,
  dataGtmInfo,
  children,
}) => {
  if (href) {
    return (
      <SALink
        href={href}
        tabIndex={-1}
        className={classNames(styles.filterItemLink, {
          [styles.filterItemLinkHidden]: isHidden && !isShowMoreOpen,
        })}
        data-gtm-event={dataGtmEvent}
        data-gtm-info={dataGtmInfo}
      >
        {children}
      </SALink>
    )
  }

  return children
}

const CheckboxListFilter: FC<PropsWithChildren<CheckboxListFilterProps>> = ({
  filterOptions,
  disableShowMore,
  children,
  facetName = '',
}) => {
  const [isShowMoreOpen, setIsShowMoreOpen] = useState(false)

  const handleToggleShowMore = () => {
    setIsShowMoreOpen((prevValue) => !prevValue)
  }

  const hasHiddenOption = useMemo(() => filterOptions.some(({ isHidden }) => !!isHidden), [
    filterOptions,
  ])

  return (
    <div className={styles.listFilterWrapper}>
      <div className={styles.listFilterContainer}>
        {filterOptions.map(({ text, checked, onClick, isHidden, href }) => (
          <CheckBoxParent
            href={href}
            isHidden={isHidden}
            isShowMoreOpen={isShowMoreOpen}
            dataGtmEvent='filter'
            dataGtmInfo={`${facetName} - ${text}`}
          >
            <Checkbox
              key={text}
              customLabelStyle={styles.customCheckboxLabelStyle}
              name={text}
              text={text}
              checked={checked}
              onClick={onClick}
            />
          </CheckBoxParent>
        ))}
      </div>

      {!disableShowMore && hasHiddenOption && (
        <div className={styles.collapsableFilterShowMore}>
          <button onClick={handleToggleShowMore}>
            {isShowMoreOpen ? 'Show Less' : 'Show More'}
          </button>
        </div>
      )}

      {children}
    </div>
  )
}

export default CheckboxListFilter
